import { Link } from "react-router-dom";
import { Plus, Trash2, Star, CheckCircle2, MessageCircle, Copy, Link2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { ICONS, COLORS } from "@/components/appbuilder/missions";
import { inputClass } from "@/components/appbuilder/MissionsA";

const newScreen = (n) => ({ id: `s${Date.now().toString(36)}${n}`, title: "", color: Object.keys(COLORS)[n % 6], icon: Object.keys(ICONS)[n % 12], text: "", buttons: [] });

export const ScreensMission = ({ project, update, tx }) => {
  const screens = project.screens;
  const edit = (i, fields) => update({ screens: screens.map((s, j) => (j === i ? { ...s, ...fields } : s)) });
  return (
    <div className="space-y-4">
      {screens.map((s, i) => (
        <div key={s.id} className="bg-sky-50 rounded-2xl p-3 space-y-2" data-testid={`screen-editor-${i}`}>
          <div className="flex justify-between items-center">
            <span className="font-fredoka font-bold text-blue-950">{tx.screen} {i + 1}</span>
            {i > 0 && <button type="button" onClick={() => update({ screens: screens.filter((_, j) => j !== i).map((x) => ({ ...x, buttons: x.buttons.filter((b) => b.target !== s.id) })) })} className="text-rose-500" aria-label="delete" data-testid={`screen-delete-${i}`}><Trash2 className="w-4 h-4" /></button>}
          </div>
          <input value={s.title} onChange={(e) => edit(i, { title: e.target.value.slice(0, 40) })} placeholder={tx.titlePh} className={inputClass} data-testid={`screen-title-${i}`} />
          <textarea value={s.text} onChange={(e) => edit(i, { text: e.target.value.slice(0, 200) })} placeholder={tx.textPh} rows={2} className={inputClass} data-testid={`screen-text-${i}`} />
          <div className="flex flex-wrap gap-1.5">
            {Object.keys(COLORS).map((c) => (
              <button key={c} type="button" onClick={() => edit(i, { color: c })} className={`${COLORS[c].bg} w-7 h-7 rounded-full border-4 ${s.color === c ? "border-blue-600" : "border-white"}`} aria-label={c} data-testid={`screen-color-${i}-${c}`} />
            ))}
            {Object.entries(ICONS).slice(0, 8).map(([k, Icon]) => (
              <button key={k} type="button" onClick={() => edit(i, { icon: k })} className={`w-7 h-7 rounded-lg flex items-center justify-center ${s.icon === k ? "bg-blue-600 text-white" : "bg-white text-blue-950"}`} aria-label={k}><Icon className="w-4 h-4" /></button>
            ))}
          </div>
        </div>
      ))}
      {screens.length < 4 && (
        <button type="button" onClick={() => update({ screens: [...screens, newScreen(screens.length)] })} className="bg-sky-500 text-white rounded-full px-4 py-2 font-bold inline-flex items-center gap-2 shadow" data-testid="screen-add-btn">
          <Plus className="w-4 h-4" /> {tx.addScreen}
        </button>
      )}
    </div>
  );
};

export const ButtonsMission = ({ project, update, tx }) => {
  const screens = project.screens;
  if (screens.length < 2) return <p className="font-bold text-blue-950">{tx.needScreens}</p>;
  const setButtons = (i, buttons) => update({ screens: screens.map((s, j) => (j === i ? { ...s, buttons } : s)) });
  return (
    <div className="space-y-4">
      {screens.map((s, i) => (
        <div key={s.id} className="bg-green-50 rounded-2xl p-3 space-y-2" data-testid={`buttons-editor-${i}`}>
          <p className="font-fredoka font-bold text-blue-950">{tx.screen} {i + 1}: {s.title}</p>
          {s.buttons.map((b, k) => (
            <div key={k} className="flex flex-wrap gap-2 items-center">
              <input value={b.label} onChange={(e) => setButtons(i, s.buttons.map((x, y) => (y === k ? { ...x, label: e.target.value.slice(0, 30) } : x)))} placeholder={tx.btnLabel} className={`${inputClass} flex-1 min-w-[8rem]`} data-testid={`btn-label-${i}-${k}`} />
              <select value={b.target} onChange={(e) => setButtons(i, s.buttons.map((x, y) => (y === k ? { ...x, target: e.target.value } : x)))} className="bg-white border-4 border-sky-100 rounded-2xl px-2 py-2 font-semibold" data-testid={`btn-target-${i}-${k}`}>
                {screens.filter((t) => t.id !== s.id).map((t) => <option key={t.id} value={t.id}>{tx.goTo}: {t.title || t.id}</option>)}
              </select>
              <button type="button" onClick={() => setButtons(i, s.buttons.filter((_, y) => y !== k))} className="text-rose-500" aria-label="delete"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {s.buttons.length < 3 && (
            <button type="button" onClick={() => setButtons(i, [...s.buttons, { label: "", target: screens.find((t) => t.id !== s.id).id }])} className="bg-green-500 text-white rounded-full px-3 py-1.5 text-sm font-bold inline-flex items-center gap-1" data-testid={`btn-add-${i}`}>
              <Plus className="w-4 h-4" /> {tx.addBtn}
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

export const TestMission = ({ tested, tx }) => (
  <p className={`rounded-2xl p-4 font-bold flex items-center gap-2 ${tested ? "bg-lime-200 text-green-900" : "bg-sky-100 text-blue-950"}`} data-testid="test-status">
    {tested && <CheckCircle2 className="w-6 h-6" />} {tested ? tx.tested : tx.tryIt}
  </p>
);

export const FeedbackMission = ({ project, update, tx, lang }) => {
  const fb = project.feedback || { who: "", rating: 0, comment: "" };
  const set = (k, v) => update({ feedback: { ...fb, [k]: v } });
  const q = lang === "es" ? `Dame tu opinión de mi app "${project.name}": ${project.idea}. ¿Qué puedo mejorar?` : `Give me feedback on my app "${project.name}": ${project.idea}. What can I improve?`;
  return (
    <div className="space-y-3">
      <input value={fb.who} onChange={(e) => set("who", e.target.value.slice(0, 30))} placeholder={tx.who} className={inputClass} data-testid="feedback-who" />
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" onClick={() => set("rating", n)} aria-label={`${n}`} data-testid={`feedback-star-${n}`}>
            <Star className={`w-9 h-9 ${n <= fb.rating ? "text-yellow-400 fill-yellow-400" : "text-slate-300"}`} />
          </button>
        ))}
      </div>
      <textarea value={fb.comment} onChange={(e) => set("comment", e.target.value.slice(0, 200))} placeholder={tx.comment} rows={2} className={inputClass} data-testid="feedback-comment" />
      <Link to={`/chat/spark?q=${encodeURIComponent(q)}`} className="inline-flex items-center gap-2 bg-blue-600 text-white rounded-full px-4 py-2 font-bold text-sm" data-testid="feedback-ask-spark">
        <MessageCircle className="w-4 h-4" /> {tx.askSpark}
      </Link>
    </div>
  );
};

export const ImproveMission = ({ project, update, tx }) => (
  <div className="space-y-2">
    <textarea value={project.improvement} onChange={(e) => update({ improvement: e.target.value.slice(0, 300) })} placeholder={tx.improvePh} rows={3} className={inputClass} data-testid="improve-input" />
    <p className="text-sm font-semibold text-blue-900">{tx.improveTip}</p>
  </div>
);

export const ShareMission = ({ project, share, tx }) => {
  const url = project.share_id ? `${window.location.origin}/mi-app/${project.share_id}` : "";
  const copy = async () => { await navigator.clipboard.writeText(url); toast.success(tx.copied); };
  return (
    <div className="space-y-3">
      {!project.shared ? (
        <button type="button" onClick={share} className="glossy-btn bg-fuchsia-500 text-white font-bold px-6 py-3 rounded-full inline-flex items-center gap-2" data-testid="share-create-btn">
          <Link2 className="w-5 h-5" /> {tx.shareBtn}
        </button>
      ) : (
        <div className="flex flex-wrap gap-2 items-center">
          <a href={url} target="_blank" rel="noopener noreferrer" className="bg-fuchsia-50 rounded-2xl px-4 py-2 font-bold text-fuchsia-700 break-all" data-testid="share-link">{url}</a>
          <button type="button" onClick={copy} className="bg-white rounded-full px-4 py-2 font-bold text-blue-900 inline-flex items-center gap-1 shadow" data-testid="share-copy-btn"><Copy className="w-4 h-4" /> {tx.copy}</button>
        </div>
      )}
      <p className="text-sm font-semibold text-blue-900 inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-green-600" /> {tx.shareNote}</p>
    </div>
  );
};
